'use client';

/**
 * PERFORMANCE ONE-FOCUS DECK — sites are the cards (/api/seo/overview, GSC
 * live). Focus: WHY = clicks/impressions/position deltas + top query,
 * PREVIEW = real query + page tables (/api/seo/[domain]), DECIDE = force GSC
 * sync (POST /api/seo/sync) + open site + jump to Improve deck.
 */
import { useCallback, useEffect, useState } from 'react';
import { OneFocusDeck, bigVerb, CY, type OFItem, type OFAdapter } from './OneFocus';
import { mono, display } from '@/future/primitives';

const delta = (v?: number) => (v == null || v === 0 ? '' : `${v > 0 ? '↑' : '↓'}${Math.abs(v)}`);
const dCol = (v?: number, flip = false) => (v == null || v === 0 ? '#56788c' : (flip ? v < 0 : v > 0) ? '#5df2b4' : '#ff7d8a');

function Why({ item }: { item: OFItem }) {
  const s = item.raw;
  const rows: [string, string, string, string][] = [
    ['CLICKS', String(s.clicks ?? 0), delta(s.clicksChange), dCol(s.clicksChange)],
    ['IMPRESSIONS', String(s.impressions ?? 0), delta(s.impressionsChange), dCol(s.impressionsChange)],
    ['AVG POSITION', s.avgPosition != null ? Number(s.avgPosition).toFixed(1) : '—', delta(s.positionChange), dCol(s.positionChange, true)],
    ['AVG CTR', s.avgCtr != null ? `${Number(s.avgCtr).toFixed(2)}%` : '—', '', '#56788c'],
  ];
  return (
    <>
      {rows.map(([l, v, d, c]) => (
        <div key={l} className="flex items-baseline justify-between" style={{ padding: '6px 0' }}>
          <span style={{ ...mono(15, '.16em'), color: '#7fa9bd' }}>{l}</span>
          <span>
            <span style={{ fontWeight: 200, fontSize: 38, color: '#aef0ff', textShadow: '0 0 12px rgba(0,240,255,.3)' }}>{v}</span>
            {d && <span style={{ ...mono(16), color: c, marginLeft: 8 }}>{d}</span>}
          </span>
        </div>
      ))}
      {s.topQuery && (
        <div style={{ marginTop: 14, borderTop: '1px solid rgba(125,232,255,.12)', paddingTop: 10 }}>
          <div style={{ ...mono(14, '.26em'), color: '#49677a', marginBottom: 6 }}>TOP QUERY</div>
          <div style={{ ...display(26, 300), color: '#d7f2ff' }}>&ldquo;{s.topQuery}&rdquo;</div>
        </div>
      )}
    </>
  );
}

function Preview({ item }: { item: OFItem }) {
  const domain = item.raw.domain;
  const [detail, setDetail] = useState<any>(null);
  useEffect(() => { fetch(`/api/seo/${domain}`).then(r => r.json()).then(setDetail).catch(() => { }); }, [domain]);
  const table = (title: string, rows: any[], keyField: string) => (
    <div style={{ marginBottom: 16 }}>
      <div style={{ ...mono(14, '.26em'), color: '#49677a', marginBottom: 8 }}>{title} · TOP {Math.min(rows.length, 8)}</div>
      {rows.slice(0, 8).map((r: any, i: number) => (
        <div key={i} className="flex items-center" style={{ gap: 10, padding: '4px 0', borderTop: i ? '1px solid rgba(125,232,255,.06)' : 'none' }}>
          <span style={{ ...display(22, 300), color: '#c4d8e4', flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{(r[keyField] || '').replace(/https?:\/\/[^/]+/, '') || r[keyField]}</span>
          <span style={{ ...mono(16), color: '#aef0ff', flexShrink: 0 }}>{r.clicks ?? 0} CL</span>
          <span style={{ ...mono(16), color: '#7fa9bd', flexShrink: 0 }}>{r.impressions ?? 0} IMP</span>
          <span style={{ ...mono(16), color: '#56788c', flexShrink: 0 }}>POS {r.position != null ? Number(r.position).toFixed(1) : '—'}</span>
        </div>
      ))}
      {!rows.length && <div style={{ ...display(22, 300), color: '#56788c' }}>Nothing in this window.</div>}
    </div>
  );
  return detail ? (
    <>
      {table('QUERIES', detail.queries || [], 'query')}
      {table('PAGES', detail.pages || [], 'page')}
    </>
  ) : <div style={{ ...display(23, 300), color: '#56788c' }}>Pulling GSC detail…</div>;
}

function Decide({ item, refresh, speak }: { item: OFItem; refresh: () => void; speak: (t: string) => void }) {
  const s = item.raw;
  const [syncing, setSyncing] = useState(false);
  const [done, setDone] = useState(false);
  const sync = async () => {
    if (syncing) return;
    setSyncing(true);
    try {
      await fetch('/api/seo/sync', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ domain: s.domain, force: true }) });
      refresh(); setDone(true); speak(`Fresh Search Console data pulled for ${s.domain}.`); setTimeout(() => setDone(false), 3000);
    } finally { setSyncing(false); }
  };
  return (
    <>
      <div onMouseUp={sync} style={{ ...bigVerb('#5fd4ef', 'rgba(0,240,255,.45)'), opacity: syncing ? .6 : 1, textAlign: 'center' }}>
        {syncing ? '⟳ PULLING GSC…' : done ? '✓ SYNCED' : '⟳ FORCE GSC SYNC'}
      </div>
      <a href={`https://${s.domain}`} target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }}>
        <div style={{ width: '100%', padding: '10px 0', marginTop: 10, borderRadius: 12, background: 'transparent', border: '1px solid rgba(125,232,255,.3)', color: CY, ...mono(16, '.26em'), textAlign: 'center', cursor: 'pointer' }}>
          OPEN {s.domain?.toUpperCase()} ↗
        </div>
      </a>
      <a href={`/future?surface=improve:wtf`} style={{ textDecoration: 'none' }}>
        <div style={{ width: '100%', padding: '10px 0', marginTop: 10, borderRadius: 12, background: 'transparent', border: '1px solid rgba(93,242,180,.3)', color: '#5df2b4', ...mono(16, '.26em'), textAlign: 'center', cursor: 'pointer' }}>
          DECISIONS → IMPROVE DECK
        </div>
      </a>
      <div style={{ ...mono(13, '.12em'), color: '#3d4654', marginTop: 10, textAlign: 'center' }}>SYNC · POST /api/seo/sync · 10-MIN THROTTLE</div>
    </>
  );
}

export function PerfDeckSurface({ domains }: { domains?: string[] }) {
  const [items, setItems] = useState<OFItem[]>([]);
  const [loaded, setLoaded] = useState(false);
  const load = useCallback(() => {
    fetch('/api/seo/overview').then(r => r.json()).then(d => {
      let sites: any[] = Array.isArray(d) ? d : d.sites || d.domains || [];
      if (domains?.length) sites = sites.filter((s: any) => domains.includes(s.domain));
      sites.sort((a: any, b: any) => (b.clicks || 0) - (a.clicks || 0));
      setItems(sites.map((s: any): OFItem => ({
        id: s.domain, title: s.domain, type: 'GSC · 28 DAYS',
        status: (s.clicksChange || 0) > 0 ? 'scaling' : undefined,
        kpis: [
          { l: 'CLICKS', v: `${s.clicks ?? 0}${delta(s.clicksChange) ? ` ${delta(s.clicksChange)}` : ''}` },
          { l: 'IMPRESSIONS', v: String(s.impressions ?? 0) },
          { l: 'POSITION', v: s.avgPosition != null ? Number(s.avgPosition).toFixed(1) : '—' },
        ],
        taste: s.topQuery ? `“${s.topQuery}”` : undefined,
        foot: s.domain, raw: s,
      })));
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, [domains]);
  useEffect(() => { load(); }, [load]);

  const adapter: OFAdapter = {
    kind: 'perf',
    strap: (its) => `ONE FOCUS · ${its.length} DOMAINS · GSC LIVE · SYNC ON OPEN`,
    Why, Preview, Decide,
  };
  return <OneFocusDeck items={items} loaded={loaded} adapter={adapter} refresh={load} />;
}
